import { TagBadge } from "@/components/ui/Tag/TagBadge";
import type { UIAudit } from "@/packages/types";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, FileText, Pencil, Tag } from "lucide-react";
import { useCallback, useState } from "react";
import { WidgetCard, WidgetCardSkeleton } from "./WidgetCard";

interface AuditMetadataCardProps {
  audit: UIAudit;
  onEditAudit?: () => void;
  onEditTags?: () => void;
  index?: number;
}

const statusLabels: Record<string, string> = {
  pending: "Pending",
  in_progress: "In Progress",
  submitted: "Submitted",
  approved: "Approved",
  rejected: "Rejected",
  archived: "Archived",
};

const statusColors: Record<string, string> = {
  pending: "bg-amber-400/10 text-amber-400 border-amber-400/20",
  in_progress: "bg-blue-400/10 text-blue-400 border-blue-400/20",
  submitted: "bg-purple-400/10 text-purple-400 border-purple-400/20",
  approved: "bg-emerald-400/10 text-emerald-400 border-emerald-400/20",
  rejected: "bg-red-400/10 text-red-400 border-red-400/20",
  archived: "bg-muted/10 text-muted border-border",
};

export function AuditMetadataCard({
  audit,
  onEditAudit,
  onEditTags,
  index,
}: AuditMetadataCardProps) {
  const [expanded, setExpanded] = useState(false);
  const tags = audit.tags ?? [];
  const visibleTags = tags.slice(0, 3);
  const hiddenTagCount = tags.length - 3;

  const handleToggle = useCallback(() => {
    setExpanded((prev) => !prev);
  }, []);

  return (
    <WidgetCard
      size="S"
      title="Details"
      icon={<FileText size={16} />}
      expandable
      expanded={expanded}
      onToggle={handleToggle}
      index={index}
      className="group"
      action={
        expanded && onEditAudit ? (
          <button
            type="button"
            className="text-muted hover:text-secondary transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              onEditAudit();
            }}
            aria-label="Edit audit"
          >
            <Pencil size={14} />
          </button>
        ) : undefined
      }
    >
      <div className="flex flex-col h-full">
        {/* Always visible: collapsed content */}
        <div className="flex flex-col gap-2">
          {/* Status badge */}
          <div>
            <span
              className={`text-[11px] px-1.5 py-0.5 rounded border ${statusColors[audit.status] ?? "bg-muted/10 text-muted border-border"}`}
            >
              {statusLabels[audit.status] ?? audit.status}
            </span>
          </div>

          {/* Tags */}
          {tags.length > 0 && (
            <div className="flex items-center gap-1 flex-wrap">
              <Tag size={12} className="text-muted shrink-0" />
              {visibleTags.map((tag) => (
                <TagBadge
                  key={tag.id}
                  name={tag.name}
                  color={tag.color}
                  onClick={onEditTags}
                />
              ))}
              {hiddenTagCount > 0 && (
                <span className="text-[11px] text-muted">
                  +{hiddenTagCount} more
                </span>
              )}
            </div>
          )}

          {/* Description (always visible) */}
          {audit.description && (
            <p className="text-xs text-secondary leading-relaxed line-clamp-2">
              {audit.description}
            </p>
          )}
        </div>

        {/* Expanded content */}
        <AnimatePresence>
          {expanded && (
            <motion.div
              key="expanded-content"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 8 }}
              transition={{ delay: 0.05, duration: 0.2 }}
              className="mt-3 flex flex-col gap-2.5 overflow-y-auto"
            >
              {/* Created date */}
              <div className="flex items-center gap-1.5">
                <span className="text-[11px] font-medium uppercase tracking-wider text-muted">
                  Created
                </span>
                <span className="text-xs text-secondary">
                  {new Date(audit.createdAt).toLocaleDateString()}
                </span>
              </div>

              {/* Notes */}
              {audit.notes && (
                <div>
                  <span className="text-[11px] font-medium uppercase tracking-wider text-muted">
                    Notes
                  </span>
                  <p className="text-xs text-secondary leading-relaxed mt-0.5 line-clamp-3">
                    {audit.notes}
                  </p>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Chevron toggle — bottom right */}
        <div className="mt-auto flex justify-end pt-1">
          <motion.button
            type="button"
            className="text-muted opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={(e) => {
              e.stopPropagation();
              handleToggle();
            }}
            animate={{ rotate: expanded ? 180 : 0 }}
            transition={{ duration: 0.2 }}
            aria-label={expanded ? "Collapse details" : "Expand details"}
          >
            <ChevronDown size={14} />
          </motion.button>
        </div>
      </div>
    </WidgetCard>
  );
}

export function AuditMetadataCardSkeleton({ index }: { index?: number }) {
  return <WidgetCardSkeleton size="S" index={index} />;
}
